import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { APIRoute } from 'routes/api'
import { useAppDispatch, RootState } from 'store'
import { getGame } from 'store/game/thunks'
import { selectParticipantRoundsForChallengeLeaderboard } from 'store/participantRounds/slice'
import { getParticipantRounds } from 'store/participantRounds/thunks'
import { getUserRounds } from 'store/user/thunks'
import { apiGET } from 'utils/apiClient'

const useParticipantRounds = (challengeId: string, participantId: string, userId: number) => {
  const dispatch = useAppDispatch()
  const selectedParticipantRounds = useSelector(selectParticipantRoundsForChallengeLeaderboard(participantId))
  const {
    userRounds: selectedUserRounds,
    userRoundsStatus,
    roundsCursor,
  } = useSelector((state: RootState) => state.user)
  const [userGames, setUserGames] = useState<Game[]>([])
  const [userRounds, setUserRounds] = useState<(UserRound & { gameCreatorName?: string })[]>([])
  const [isFetchingGames, setIsFetchingGames] = useState(false)
  const [isUpdatingUserRounds, setIsUpdatingUserRounds] = useState(false)
  const lastFetchedCursor = useRef<string | null>(null)
  const gameCreatorsCache = useRef<Map<number, User>>(new Map())
  const requestedGameIds = useRef<Set<number>>(new Set())
  const gameCache = useRef<Map<number, Game>>(new Map())

  useEffect(() => {
    requestedGameIds.current.clear()
    gameCache.current.clear()
    lastFetchedCursor.current = null
  }, [challengeId, participantId])

  useEffect(() => {
    if (challengeId && participantId) {
      dispatch(getParticipantRounds({ challengeId, participantId }))
      dispatch(getUserRounds({ id: userId }))
    }
  }, [challengeId, participantId, userId, dispatch])

  useEffect(() => {
    if (userRoundsStatus !== 'succeeded' || !selectedUserRounds.length) {
      return
    }

    let cancelled = false
    // Only the rounds in this challenge are shown, so only their creators need to be looked up
    const participantRoundIds = new Set((selectedParticipantRounds?.rounds ?? []).map((round) => String(round.roundId)))
    const challengeRounds = selectedUserRounds.filter((round) => participantRoundIds.has(String(round.roundId)))
    const missingCreatorIds = [
      ...new Set(
        challengeRounds
          .map((round) => round.creatorId)
          .filter((creatorId): creatorId is number => !!creatorId && !gameCreatorsCache.current.has(creatorId)),
      ),
    ]

    const mapGameCreatorIdToName = async () => {
      setIsUpdatingUserRounds(true)

      // One request per unique creator, in parallel. A failed lookup shows '-' instead of failing every row.
      const responses = await Promise.allSettled(
        missingCreatorIds.map((creatorId) => apiGET({ url: APIRoute.GET_USER(String(creatorId)) })),
      )
      responses.forEach((response, index) => {
        if (response.status === 'fulfilled') {
          gameCreatorsCache.current.set(missingCreatorIds[index], response.value.data as User)
        }
      })

      if (cancelled) {
        return
      }

      setUserRounds(
        challengeRounds.map((round) => {
          if (!round.creatorId) {
            return { ...round, gameCreatorName: '' }
          }
          const creator = gameCreatorsCache.current.get(round.creatorId)
          return {
            ...round,
            gameCreatorName: `${creator?.firstName ?? ''} ${creator?.lastName ?? ''}`.trim() || '-',
          }
        }),
      )
      setIsUpdatingUserRounds(false)
    }

    mapGameCreatorIdToName()

    return () => {
      cancelled = true
    }
  }, [selectedUserRounds, userRoundsStatus, selectedParticipantRounds])

  const roundGames = useCallback(async () => {
    if (!selectedUserRounds?.length || !selectedParticipantRounds?.rounds?.length) {
      return []
    }

    const participantRoundIds = new Set((selectedParticipantRounds.rounds ?? []).map((round) => String(round.roundId)))

    const uniqueGameIds = [
      ...new Set(
        selectedUserRounds
          .filter((round) => participantRoundIds.has(String(round.roundId)))
          .map((round) => round.eventId)
          .filter((eventId): eventId is number => eventId !== undefined),
      ),
    ]

    const missingGameIds = uniqueGameIds.filter((gameId) => !requestedGameIds.current.has(gameId))

    const gameResponses = await Promise.allSettled(missingGameIds.map((gameId) => dispatch(getGame(gameId)).unwrap()))
    gameResponses.forEach((response, index) => {
      if (response.status === 'fulfilled') {
        requestedGameIds.current.add(missingGameIds[index])
        gameCache.current.set(missingGameIds[index], response.value)
      }
    })

    const games = selectedUserRounds
      .filter((round) => participantRoundIds.has(String(round.roundId)))
      .map((round) => {
        const eventId = round.eventId
        if (eventId === undefined) {
          return undefined
        }

        return gameCache.current.get(eventId) ?? undefined
      })
      .filter((game): game is Game => !!game)
    games.sort((a, b) => {
      const aRound = selectedUserRounds.find((round) => String(round.eventId) === a.id)
      const bRound = selectedUserRounds.find((round) => String(round.eventId) === b.id)
      return (aRound?.roundId ?? 0) - (bRound?.roundId ?? 0)
    })
    return games
  }, [selectedParticipantRounds, selectedUserRounds, dispatch])

  useEffect(() => {
    const fetchRoundsGames = async () => {
      if (!selectedUserRounds?.length) {
        setUserGames([])
        return
      }

      setIsFetchingGames(true)
      try {
        const games = await roundGames()
        setUserGames(games)
      } finally {
        setIsFetchingGames(false)
      }
    }

    fetchRoundsGames()
  }, [selectedUserRounds])

  // Participant rounds are not paginated, but the user rounds they are matched against are.
  // Keep fetching user round pages until every participant round is found or there are no more pages.
  // Deleted rounds are never in the user's rounds, so waiting for them would page through the whole history.
  const hasUnmatchedParticipantRounds = useMemo(() => {
    const loadedRoundIds = new Set(selectedUserRounds.map((round) => String(round.roundId)))
    return (selectedParticipantRounds?.rounds ?? [])
      .filter(
        (round) =>
          !round.reasons?.some(
            (reason) => reason.rule === 'ROUND_ELIGIBILITY_RULE_ROUND_NOT_DELETED' && reason.eligible === false,
          ),
      )
      .some((round) => !loadedRoundIds.has(String(round.roundId)))
  }, [selectedParticipantRounds, selectedUserRounds])

  const isLoadingMoreRounds =
    selectedParticipantRounds?.status === 'succeeded' &&
    userRoundsStatus === 'succeeded' &&
    hasUnmatchedParticipantRounds &&
    !!roundsCursor

  useEffect(() => {
    if (userId && isLoadingMoreRounds && roundsCursor && lastFetchedCursor.current !== roundsCursor) {
      lastFetchedCursor.current = roundsCursor
      dispatch(getUserRounds({ id: userId, params: { cursor: roundsCursor } }))
    }
  }, [userId, isLoadingMoreRounds, roundsCursor, dispatch])

  const memoizedUserRounds = useMemo(() => {
    const participantRoundsById = new Map(
      (selectedParticipantRounds?.rounds ?? []).map((round) => [String(round.roundId), round]),
    )
    const uniqueGames = [...new Map(userGames.map((game) => [game.id, game])).values()]

    return uniqueGames.flatMap((game) => {
      const userRound = userRounds.find((round) => String(round.eventId) === game.id)
      if (!userRound) {
        return []
      }

      // A multiround event holds its rounds inside the game object
      const roundIds = userRound.isMultiround
        ? game.rounds.map((round) => String(round.id))
        : [String(userRound.roundId)]

      return roundIds.flatMap((roundId) => {
        const participantRound = participantRoundsById.get(roundId)
        // Each multiround round has its own user round (own name, start time), the event's one is only a fallback
        const roundUserRound = userRounds.find((round) => String(round.roundId) === roundId) ?? userRound
        return participantRound ? [{ participantRound, userRound: roundUserRound, game }] : []
      })
    })
  }, [selectedParticipantRounds, userGames, userRounds])

  return {
    userRounds: memoizedUserRounds,
    loading:
      selectedParticipantRounds?.status === 'loading' ||
      userRoundsStatus === 'loading' ||
      isUpdatingUserRounds ||
      isFetchingGames ||
      isLoadingMoreRounds,
  }
}

export default useParticipantRounds
